import React, { useCallback, useEffect, useState, useMemo, useRef, useContext } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import VerticalCard from '../components/VerticalCard'
import NotFound from '../components/NotFound'
import SummaryApi from '../common'
import { FaFilter, FaChevronUp, FaChevronDown, FaStar, FaAngleRight, FaAngleLeft } from 'react-icons/fa'
import service from '../common/HttpService'
import Pagination from '../components/Pagination'
import useTranslate from '../hooks/useTranslate'
import Context from '../context'

const CategoryProduct = () => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)

    // Server-side Pagination State
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const itemsPerPage = Number(process.env.REACT_APP_PAGINATION_LIMIT) || 20

    const navigate = useNavigate()
    const location = useLocation()
    const urlSearch = useMemo(() => new URLSearchParams(location.search), [location.search])
    const { translateText, languageCode } = useContext(Context)

    const t = useTranslate({
        home: "Home",
        ourCatalog: "Our Catalog",
        filters: "Filters",
        reset: "Reset",
        categories: "Categories",
        subCategories: "Sub Categories",
        brands: "Brands",
        price: "Price",
        min: "Min",
        max: "Max",
        sortBy: "Sort By",
        priceLowToHigh: "Price (Low to High)",
        priceHighToLow: "Price (High to Low)",
        productsCatalog: "Products Catalog",
        showing: "Showing",
        of: "of",
        items: "items",
        page: "Page",
        noProducts: "No products found in the database",
        loadingCategories: "Loading categories...",
        loadingBrands: "Loading brands..."
    });

    const [translatedCategories, setTranslatedCategories] = useState([])
    const [translatedBrands, setTranslatedBrands] = useState([])
    const [translatedSubCategories, setTranslatedSubCategories] = useState([])
    const [translatedCatalogHeading, setTranslatedCatalogHeading] = useState("Products Catalog")

    // Filter Options
    const [apiCategories, setApiCategories] = useState([])
    const [apiBrands, setApiBrands] = useState([])
    const [maxPrice, setMaxPrice] = useState(20000)

    // Selection State
    const [selectCategory, setSelectCategory] = useState({})
    const [selectSubCategory, setSelectSubCategory] = useState({})
    const [selectBrand, setSelectBrand] = useState({})
    const [selectedPrice, setSelectedPrice] = useState(20000)
    const [sortBy, setSortBy] = useState("")

    const [apiSubCategories, setApiSubCategories] = useState([])

    // Refs for stable access in fetch callback without dependency loops
    const apiCategoriesRef = useRef([])
    const apiSubCategoriesRef = useRef([])
    const apiBrandsRef = useRef([])
    const [subcategoriesLoading, setSubcategoriesLoading] = useState(false)

    const [openSections, setOpenSections] = useState({
        categories: true,
        subcategories: true,
        brands: true,
        price: true,
        rating: true,
        availability: true
    })

    // 1. Fetch Filter Options (Categories and Brands from dedicated APIs)
    const fetchFilterOptions = useCallback(async () => {
        try {
            // Fetch Categories from dedicated API
            const categoryResponse = await service.get(SummaryApi.getCategory.url)
            if (categoryResponse?.status === "1" || categoryResponse?.success === "true" || categoryResponse?.success === true) {
                const catsFromApi = categoryResponse?.extraData?.category || categoryResponse?.data || []
                const catList = catsFromApi.map(cat => ({
                    label: cat.category || cat.name || cat.label || cat.value,
                    value: (cat.category || cat.name || cat.label || cat.value || "").toLowerCase(),
                    id: cat.id || cat._id
                })).filter(c => c.label).sort((a, b) => a.label.localeCompare(b.label))
                setApiCategories(catList)
                apiCategoriesRef.current = catList
            }

            // Fetch Brands from dedicated API
            const brandResponse = await service.get(SummaryApi.getBrand.url)
            console.log("Brand API Response:", brandResponse)
            if (brandResponse?.status === "1" || brandResponse?.success === "true" || brandResponse?.success === true) {
                const brandsFromApi = brandResponse?.extraData?.brand || brandResponse?.data || []
                const brandObjects = brandsFromApi.map(b => ({
                    id: b.id || b._id,
                    name: b.name || b.label || String(b)
                })).filter(o => o.name).sort((a, b) => a.name.localeCompare(b.name))
                setApiBrands(brandObjects)
                apiBrandsRef.current = brandObjects
            }

            // Max price is set to a reasonable default or can be hardcoded if needed
            // setMaxPrice(20000) 

            // Note: We don't override selectedPrice here if it was already modified or initialized
        } catch (error) {
            console.log("Error fetching filter options:", error)
        }
    }, [])

    // 2. Fetch Paginated Data from Server
    const fetchData = useCallback(async (page = 1) => {
        try {
            setLoading(true)

            // Prepare active filters
            const activeCategories = Object.keys(selectCategory).filter(key => selectCategory[key]);
            const activeSubCategories = Object.keys(selectSubCategory).filter(key => selectSubCategory[key]);
            const activeBrands = Object.keys(selectBrand).filter(key => selectBrand[key]);
            const searchQuery = urlSearch.get("q") || urlSearch.get("search") || "";

            // Identify all selected Category IDs
            const selectedCatIds = activeCategories.map(catValue => {
                const matched = apiCategories.find(c => c.value === catValue);
                return matched ? matched.id : null;
            }).filter(Boolean);

            // Identify all selected SubCategory IDs
            const selectedSubCatIds = activeSubCategories.map(subValue => {
                const matched = apiSubCategories.find(s => s.value === subValue);
                return matched ? matched.id : null;
            }).filter(Boolean);

            // Identify all selected Brand IDs
            let selectedBrandIds = activeBrands.map(brandName => {
                const matched = apiBrands.find(b => b.name === brandName);
                return matched ? matched.id : null;
            }).filter(Boolean);

            // Fallback: If selectedBrandIds is empty, but we have a brandId in the URL, use it directly
            const urlBrandId = urlSearch.get("brandId");
            if (selectedBrandIds.length === 0 && urlBrandId) {
                selectedBrandIds = [urlBrandId];
            }

            let response = { success: false };
            let fetchedProducts = [];
            const backendBaseUrl = (SummaryApi.allProduct.url || "").split('/Api')[0];
            // Base Payload for pagination API
            const payload = {
                page_no: String(page),
                lim: String(itemsPerPage)
            };
            if (activeCategories.length > 0) payload.category = activeCategories;
            if (activeSubCategories.length > 0) payload.subcategory = activeSubCategories;
            if (activeBrands.length > 0) payload.brand = activeBrands;
            if (sortBy) payload.sort = sortBy;
            if (selectedPrice < maxPrice) payload.price = String(selectedPrice);
            if (searchQuery) payload.search = searchQuery;

            // Use specific endpoint based on selection
            if (selectedSubCatIds.length > 0 && selectedCatIds.length > 0) {
                console.log("Using subcategory_id API");
                const formData = new FormData();
                formData.append('category_id', selectedCatIds[0]);
                formData.append('subcategory_id', selectedSubCatIds[0]);
                response = await service.post(SummaryApi.productBySubCategory.url, formData);
            } else if (selectedBrandIds.length > 0 && selectedCatIds.length === 0 && !searchQuery) {
                console.log("Using product_by_brand_id API");
                const formData = new FormData();
                formData.append('brand_id', selectedBrandIds[0]);
                response = await service.post(SummaryApi.productByBrand.url, formData);
            } else if (selectedCatIds.length > 0 && activeBrands.length === 0 && !searchQuery) {
                console.log("Using product_by_category_id API");
                const formData = new FormData();
                formData.append('category_id', selectedCatIds[0]);
                formData.append('cat_id', selectedCatIds[0]);
                response = await service.post(SummaryApi.productByCategory.url, formData);
            } else {
                console.log("Using Pagination API with Payload:", payload);
                response = await service.post(SummaryApi.pagination.url, payload);
            }

            if (response.success === "true" || response.success === true) {
                // extraData is an array: [0] = { page: {...} }, [1] = { pagination_data: [...] }
                const extraArr = response.extraData;
                let pageInfo = null;
                let paginationData = [];

                if (Array.isArray(extraArr)) {
                    // Find page info object
                    const pageObj = extraArr.find(item => item?.page);
                    pageInfo = pageObj?.page || null;
                    // Find pagination_data array
                    const dataObj = extraArr.find(item => Array.isArray(item?.pagination_data));
                    paginationData = dataObj?.pagination_data || [];
                } else if (extraArr && typeof extraArr === 'object') {
                    // Fallback: flat object
                    pageInfo = extraArr.page || null;
                    paginationData = extraArr.pagination_data || extraArr.product || extraArr.subcategory || extraArr.category || Object.values(extraArr);
                }

                if (pageInfo) {
                    setTotalPages(Number(pageInfo.total_page) || 1)
                    setTotalItems(Number(pageInfo.total_no_data) || 0)
                    console.log("Pagination Meta:", pageInfo)
                } else {
                    // Approximate counts if page meta is missing from product_by_category apis
                    setTotalPages(1);
                    setTotalItems(paginationData.length || 0);
                }
                fetchedProducts = Array.isArray(paginationData) ? paginationData : [];
                console.log("Products in this page:", fetchedProducts.length);
            }
            console.log("Raw fetched data count:", fetchedProducts.length)

            if (fetchedProducts.length > 0 || response?.success === "true" || response?.success === true) {
                const formatted = fetchedProducts.map(item => {
                    let images = [];
                    const itemImages = item.image || item.productImage || [];
                    if (Array.isArray(itemImages)) {
                        images = itemImages;
                    } else if (typeof itemImages === 'string' && itemImages) {
                        images = itemImages.includes(',') ? itemImages.split(',') : [itemImages];
                    }

                    // Special case: If image is just the directory "https://www.fasalshop.com/product-images/"
                    // We need to look for a better fallback or just use it as is
                    images = images.map(img => {
                        if (typeof img === 'string' && img && !img.startsWith('http')) {
                            const cleanPath = img.startsWith('/') ? img.substring(1) : img;
                            return `${backendBaseUrl}/${cleanPath}`;
                        } else if (img === "https://www.fasalshop.com/product-images/") {
                            // This is a directory path, maybe use product image or fallback
                            return null;
                        }
                        return img;
                    }).filter(Boolean);

                    if (images.length === 0) {
                        images = [''];
                    }

                    // Handle pricing which might be in rates[] or item_rates[]
                    const firstRate = (item.item_rates && item.item_rates[0]) || (item.rates && item.rates[0]) || {};

                    return {
                        ...item,
                        _id: item.id || item._id,
                        productName: item.itemname || item.productName || "",
                        productImage: images,
                        price: Number(firstRate.mrp) || Number(item.price) || 0,
                        sellingPrice: Number(firstRate.salerate) || Number(item.sellingPrice) || 0,
                        category: item.category_name || item.category,
                        brandName: item.brand_name || item.vendor_name || item.subcategory_name || item.brandName || "",
                    }
                })

                // Subcategories are now fetched dynamically based on selected categories (see useEffect below)

                setData(formatted)
            } else {
                setData([])
                setTotalItems(0)
                setTotalPages(1)
            }
            setLoading(false)
        } catch (error) {
            console.error("Error fetching paginated data:", error)
            setData([])
            setLoading(false)
        }
    }, [itemsPerPage, selectCategory, selectSubCategory, selectBrand, sortBy, selectedPrice, urlSearch, maxPrice, apiCategories, apiSubCategories, apiBrands])

    // Initialize selections from URL on mount and whenever URL changes
    useEffect(() => {
        const initialCats = {}
        const urlCats = urlSearch.getAll("category")
        urlCats.forEach(cat => {
            initialCats[cat.toLowerCase()] = true
        })

        const initialSubCats = {}
        const urlSubCats = urlSearch.getAll("subcategory")
        urlSubCats.forEach(sub => {
            initialSubCats[sub.toLowerCase()] = true
        })

        const initialBrands = {}
        const urlBrands = urlSearch.getAll("brand")
        urlBrands.forEach(br => {
            initialBrands[br] = true
        })

        setSelectCategory(prev => {
            if (JSON.stringify(prev) === JSON.stringify(initialCats)) return prev;
            return initialCats;
        })
        setSelectSubCategory(prev => {
            if (JSON.stringify(prev) === JSON.stringify(initialSubCats)) return prev;
            return initialSubCats;
        })
        setSelectBrand(prev => {
            if (JSON.stringify(prev) === JSON.stringify(initialBrands)) return prev;
            return initialBrands;
        })

        // When URL changes, we should also reset to page 1
        setCurrentPage(1)
    }, [urlSearch])

    useEffect(() => {
        fetchFilterOptions()
        // fetchData will be called by handlePageChange or by initial load inside effects
    }, [fetchFilterOptions])

    // Effect to trigger data fetch when filters or page changes
    useEffect(() => {
        fetchData(currentPage)
    }, [currentPage, fetchData]);

    // Fetch subcategories when selected categories change
    useEffect(() => {
        const fetchSubCategories = async () => {
            const activeCategories = Object.keys(selectCategory).filter(key => selectCategory[key]);

            if (activeCategories.length === 0) {
                setApiSubCategories([]);
                apiSubCategoriesRef.current = [];
                setSubcategoriesLoading(false);
                return;
            }

            const selectedCatIds = activeCategories.map(catValue => {
                const matched = apiCategoriesRef.current.find(c => c.value === catValue);
                return matched ? matched.id : null;
            }).filter(Boolean);

            if (selectedCatIds.length === 0) {
                setApiSubCategories([]);
                apiSubCategoriesRef.current = [];
                setSubcategoriesLoading(false);
                return;
            }

            setSubcategoriesLoading(true);
            try {
                const promises = selectedCatIds.map(id => {
                    const formData = new FormData();
                    formData.append("category_id", id);
                    formData.append("id", id);
                    return service.post(SummaryApi.subcategoryById.url, formData).catch(() => ({}));
                });

                const responses = await Promise.all(promises);
                const allSubs = [];

                responses.forEach(res => {
                    if (res && (res.success === "true" || res.success === true)) {
                        const rawSub = res.extraData?.subcategory;
                        // API can return single object or array
                        const subsArr = Array.isArray(rawSub) ? rawSub : (rawSub ? [rawSub] : []);
                        subsArr.forEach(sub => {
                            const name = sub.subcategory_name || sub.subcategory || sub.name || sub.label || sub.value;
                            const id = sub.id || sub._id;
                            if (name && id) {
                                allSubs.push({ label: name, value: name.toLowerCase(), id: id });
                            }
                        });
                    }
                });

                // Remove duplicates by "value"
                const uniqueSubs = Array.from(new Map(allSubs.map(item => [item.value, item])).values());
                uniqueSubs.sort((a, b) => a.label.localeCompare(b.label));

                setApiSubCategories(uniqueSubs);
                apiSubCategoriesRef.current = uniqueSubs;
            } catch (err) {
                console.error("Error fetching subcategories:", err);
            } finally {
                setSubcategoriesLoading(false);
            }
        };

        fetchSubCategories();
    }, [selectCategory, apiCategories]);

    // Translate dynamic filter options
    useEffect(() => {
        const translateFilters = async () => {
            if (!languageCode || languageCode === 'en') {
                setTranslatedCategories(apiCategories);
                setTranslatedBrands(apiBrands.map(b => b.name || b));
                setTranslatedSubCategories(apiSubCategories);
                return;
            }
            try {
                const [cats, brands, subs] = await Promise.all([
                    Promise.all(apiCategories.map(async c => ({ ...c, label: await translateText(c.label, languageCode) }))),
                    Promise.all(apiBrands.map(b => translateText(b.name || b, languageCode))),
                    Promise.all(apiSubCategories.map(async s => ({ ...s, label: await translateText(s.label, languageCode) })))
                ]);
                setTranslatedCategories(cats);
                setTranslatedBrands(brands);
                setTranslatedSubCategories(subs);
            } catch (error) {
                console.error("Filter translation error:", error);
            }
        };
        translateFilters();
    }, [languageCode, apiCategories, apiBrands, apiSubCategories, translateText]);

    // Translate dynamic catalog heading
    useEffect(() => {
        const translateHeading = async () => {
            const activeCats = Object.keys(selectCategory).filter(k => selectCategory[k]);
            const rawHeading = activeCats.length === 1 ? activeCats[0] : "Products Catalog";

            if (!languageCode || languageCode === 'en') {
                setTranslatedCatalogHeading(rawHeading);
                return;
            }
            setTranslatedCatalogHeading(await translateText(rawHeading, languageCode));
        };
        translateHeading();
    }, [languageCode, selectCategory, translateText]);

    const handleSelectCategory = (e) => {
        const { value, checked } = e.target
        setSelectCategory(prev => {
            const newState = { ...prev, [value]: checked }
            // Reset subcategories when category changes
            setSelectSubCategory({})
            setApiSubCategories([])

            // Update URL
            const active = Object.keys(newState).filter(k => newState[k])
            const params = new URLSearchParams(urlSearch)
            params.delete("category")
            params.delete("subcategory")
            active.forEach(cat => params.append("category", cat))
            navigate({ search: params.toString() }, { replace: true })
            return newState
        })
    }

    const handleSelectSubCategory = (e) => {
        const { value, checked } = e.target
        setSelectSubCategory(prev => {
            const newState = { ...prev, [value]: checked }
            // Update URL
            const active = Object.keys(newState).filter(k => newState[k])
            const params = new URLSearchParams(urlSearch)
            params.delete("subcategory")
            active.forEach(sub => params.append("subcategory", sub))
            navigate({ search: params.toString() }, { replace: true })
            return newState
        })
    }

    const handleSelectBrand = (e) => {
        const { value, checked } = e.target
        setSelectBrand(prev => {
            const newState = { ...prev, [value]: checked }
            // Update URL
            const active = Object.keys(newState).filter(k => newState[k])
            const params = new URLSearchParams(urlSearch)
            params.delete("brand")
            params.delete("brandId")
            active.forEach(br => params.append("brand", br))
            navigate({ search: params.toString() }, { replace: true })
            return newState
        })
    }

    const clearAllFilters = () => {
        setSelectCategory({})
        setSelectSubCategory({})
        setSelectBrand({})
        setSelectedPrice(maxPrice)
        setSortBy("")
        navigate({ search: "" }, { replace: true })
        setCurrentPage(1)
    }

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const toggleSection = (section) => {
        setOpenSections(prev => ({ ...prev, [section]: !prev[section] }))
    }



    return (
        <div className='bg-[#f4f7f5] min-h-screen py-6'>
            <div className='container mx-auto px-4'>

                {/* Navigation */}
                <div className='flex items-center justify-between mb-8'>
                    <div className='flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400'>
                        <Link to="/" className='hover:text-[#00b259] transition-colors'>{t.home}</Link>
                        <FaAngleRight size={10} />
                        <span className='text-[#00b259]'>{t.ourCatalog}</span>
                    </div>
                </div>

                <div className='flex flex-col lg:flex-row gap-6'>
                    {/* Sidebar */}
                    <div className='lg:w-[320px] shrink-0'>
                        <div className='bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden sticky top-24'>
                            <div className='p-6 border-b border-gray-50 flex justify-between items-center'>
                                <h2 className='text-[#143d2c] font-black text-lg flex items-center gap-2 uppercase tracking-wider'>
                                    <FaFilter size={14} className='text-[#00b259]' /> {t.filters}
                                </h2>
                                <button onClick={clearAllFilters} className='text-[#00b259] text-[10px] font-black uppercase hover:underline'>{t.reset}</button>
                            </div>

                            <div className='max-h-[calc(100vh-200px)] overflow-y-auto px-6 py-2 custom-scrollbar'>
                                <div className='py-4 border-b border-gray-50'>
                                    <button onClick={() => toggleSection('categories')} className='w-full flex justify-between items-center text-[#143d2c] font-bold text-sm uppercase mb-2'>
                                        {t.categories} {openSections.categories ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                                    </button>
                                    {openSections.categories && (
                                        <div className='space-y-2 mt-4 max-h-48 overflow-y-auto pr-2 custom-scrollbar'>
                                            {apiCategories.length > 0 ? (
                                                translatedCategories.map((category) => (
                                                    <div key={category.value} className='flex items-center gap-3'>
                                                        <input type='checkbox' checked={selectCategory[category.value] || false} value={category.value} onChange={handleSelectCategory} id={"cat-" + category.value} className='accent-[#00b259] w-4 h-4' />
                                                        <label htmlFor={"cat-" + category.value} className='text-gray-500 text-sm cursor-pointer'>{category.label}</label>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className='space-y-3'>
                                                    {[1, 2, 3, 4, 5].map(i => (
                                                        <div key={i} className='flex items-center gap-3'>
                                                            <div className='w-4 h-4 shimmer-effect rounded'></div>
                                                            <div className='h-3 shimmer-effect rounded w-3/4'></div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Subcategory Section — only shown when a category is selected */}
                                {Object.keys(selectCategory).some(k => selectCategory[k]) && (
                                    <div className='py-4 border-b border-gray-50'>
                                        <button onClick={() => toggleSection('subcategories')} className='w-full flex justify-between items-center text-[#143d2c] font-bold text-sm uppercase mb-2'>
                                            {t.subCategories} {openSections.subcategories ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                                        </button>
                                        {openSections.subcategories && (
                                            <div className='space-y-2 mt-4 max-h-48 overflow-y-auto pr-2 custom-scrollbar'>
                                                {subcategoriesLoading ? (
                                                    <div className='space-y-3'>
                                                        {[1, 2, 3].map(i => (
                                                            <div key={i} className='flex items-center gap-3'>
                                                                <div className='w-4 h-4 shimmer-effect rounded'></div>
                                                                <div className='h-3 shimmer-effect rounded w-3/4'></div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : translatedSubCategories.length > 0 ? (
                                                    translatedSubCategories.map((sub) => (
                                                        <div key={sub.value} className='flex items-center gap-3'>
                                                            <input type='checkbox' checked={selectSubCategory[sub.value] || false} value={sub.value} onChange={handleSelectSubCategory} id={"sub-" + sub.value} className='accent-[#00b259] w-4 h-4' />
                                                            <label htmlFor={"sub-" + sub.value} className='text-gray-500 text-sm cursor-pointer'>{sub.label}</label>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className='text-xs text-gray-400 italic'>No subcategories found</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className='py-4 border-b border-gray-50'>
                                    <button onClick={() => toggleSection('brands')} className='w-full flex justify-between items-center text-[#143d2c] font-bold text-sm uppercase mb-4'>
                                        {t.brands} {openSections.brands ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                                    </button>
                                    {openSections.brands && (
                                        <div className='space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2'>
                                            {apiBrands.length > 0 ? (
                                                translatedBrands.map((brand, idx) => {
                                                    const brandName = apiBrands[idx]?.name || apiBrands[idx] || '';
                                                    return (
                                                        <div key={idx} className='flex items-center gap-3'>
                                                            <input type='checkbox' checked={selectBrand[brandName] || false} onChange={handleSelectBrand} value={brandName} id={"brand-" + idx} className='accent-[#00b259]' />
                                                            <label htmlFor={"brand-" + idx} className='text-gray-500 text-sm'>{brand}</label>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className='space-y-3'>
                                                    {[1, 2, 3, 4, 5].map(i => (
                                                        <div key={i} className='flex items-center gap-3'>
                                                            <div className='w-4 h-4 shimmer-effect rounded'></div>
                                                            <div className='h-3 shimmer-effect rounded w-3/4'></div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className='py-4'>
                                    <button onClick={() => toggleSection('price')} className='w-full flex justify-between items-center text-[#143d2c] font-bold text-sm uppercase mb-4'>
                                        {t.price} {openSections.price ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                                    </button>
                                    {openSections.price && (
                                        <div className='px-2'>
                                            <input type='range' min="0" max={maxPrice} value={selectedPrice} onChange={(e) => setSelectedPrice(Number(e.target.value))} className='w-full h-1.5 bg-gray-100 rounded-lg appearance-none accent-[#00b259]' />
                                            <div className='flex justify-between mt-4 text-[10px] font-black text-[#143d2c] uppercase'>
                                                <span>{t.min}: ₹0</span>
                                                <span>{t.max}: ₹{selectedPrice}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Products Area */}
                    <div className='flex-1'>
                        <div className='bg-white rounded-[32px] p-6 mb-6 border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4'>
                            <div>
                                <h1 className='text-[#143d2c] text-xl font-black mb-1 tracking-tight uppercase'>
                                    {translatedCatalogHeading}
                                </h1>
                                <p className='text-gray-400 text-[11px] font-black uppercase tracking-widest'>
                                    {t.showing} {data.length} {t.of} {totalItems} {t.items} ({t.page} {currentPage} / {totalPages})
                                </p>
                            </div>

                            <div className='flex items-center gap-4 bg-[#f8fcf9] p-2 rounded-2xl border border-[#eef5ef]'>
                                <select className='bg-transparent text-[#143d2c] text-[10px] font-black py-2 px-4 focus:outline-none cursor-pointer uppercase tracking-widest' value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                    <option value="">{t.sortBy}</option>
                                    <option value="asc">{t.priceLowToHigh}</option>
                                    <option value="dsc">{t.priceHighToLow}</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            {loading ? (
                                <VerticalCard loading={true} />
                            ) : (
                                data.length > 0 ? (
                                    <>
                                        <VerticalCard data={data} loading={false} />

                                        {/* Using the new reusable Pagination Component */}
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            totalItems={totalItems}
                                            itemsPerPage={itemsPerPage}
                                            onPageChange={handlePageChange}
                                        />
                                    </>
                                ) : (
                                    <div className='bg-white rounded-[32px] p-20 text-center border border-dashed border-gray-200'>
                                        <p className='text-gray-400 font-bold uppercase text-xs tracking-widest'>{t.noProducts}</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}


export default CategoryProduct